import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { logout } from '../../store/authSlice';
import { Container, TextField, Button, Box, Typography, Alert, IconButton, InputAdornment, Link } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PasswordRequirements from '../PasswordRequirements';
import { runtime } from '../../platform/runtime';
import api from '../../utils/axiosConfig';
import { toast } from 'react-toastify';



const ResetPassword = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [password, setPassword] = useState('');
    const [sendLinkAgain, setSendLinkAgain] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        const passwordValidationResult = validatePassword(password);
        if (passwordValidationResult !== true) {
            setError(passwordValidationResult);
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            console.error("Passwords don't match");
            return;
        }

        setSendLinkAgain(false);
        try {
            if (!token) {
                setError('Token expired');
                return;
            }
            const body = { password, confirmPassword };
            // Using custom headers for this specific request (overriding the global token)
            const response = await api.post(`/api/forgotPassword/reset`, body, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            setError('');
            const data = response.data;
            if (data.success) {
                toast.success('Password reset successful!');
                dispatch(logout());
                navigate('/login'); // Redirect to first login flow(Eula and reset password)
            } else {
                setError('Invalid login response. Please try again.');
            }
        } catch (error) {
            if (error.response?.status === 401) setSendLinkAgain(true);
            if (error.response?.data?.err) {
                if (error.response.data.err.includes('expired')) {
                    setSendLinkAgain(true);
                }
                setError(error.response.data.err);
            } else if (error.response && error.response.status === 401) {
                setError('Token may have expired. Please request a new password reset link.');
            } else if (error.response && error.response.status === 500) {
                setError('Server error. Please try again later.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        }
    };

    const validatePassword = (password) => {
        const lengthCheck = /^.{8,30}$/;
        const digitCheck = /\d/;
        const upperCaseCheck = /[A-Z]/;
        const lowerCaseCheck = /[a-z]/;
        const specialCharacterCheck = /[!@#$%^&*(),.?":{}|<>]/;
        let err = '';
        if (!lengthCheck.test(password)) {
            err += 'Password must be between 8 and 30 characters in length. ';
        }
        if (!digitCheck.test(password)) {
            err += 'Password must include at least one digit. ';
        }
        if (!upperCaseCheck.test(password) || !lowerCaseCheck.test(password)) {
            err += 'Password must include both upper and lower case characters. ';
        }
        if (!specialCharacterCheck.test(password)) {
            err += 'Password must include at least one special character.';
        }
        if (!err) {
            return true;  // Return true if all checks pass
        } else {
            return err;
        }
    };

    return (

        <Container maxWidth="xs">
            {token ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="90vh"
                >
                    <Box sx={{ position: 'relative' }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Reset Password
                        </Typography>
                    </Box>
                    {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
                    {sendLinkAgain && <Link component={RouterLink} to="/forgotPassword"
                        sx={{ minHeight: 48, display: 'inline-flex', alignItems: 'center', mb: 2 }}>
                        Request a new password reset link
                    </Link>}
                    <PasswordRequirements id="reset-password-requirements" />
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="password"
                            inputProps={{ 'aria-describedby': 'reset-password-requirements' }}
                            autoFocus={runtime.isWeb()}
                            autoComplete="new-password"
                            value={password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            type="button"
                                            aria-label={showConfirmPassword ? 'Hide passwords' : 'Show passwords'}
                                            sx={{ minWidth: 48, minHeight: 48 }}
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}  // Toggle showConfirmPassword state
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            id="confirmPassword"
                            value={confirmPassword}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            type="button"
                                            aria-label={showConfirmPassword ? 'Hide passwords' : 'Show passwords'}
                                            sx={{ minWidth: 48, minHeight: 48 }}
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}  // Toggle showConfirmPassword state
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ mt: 3, mb: 2, minHeight: 48 }}
                        >
                            Update Password
                        </Button>
                    </Box>
                    <Link component={RouterLink} to="/login" variant="body2" sx={{ mt: 1, minHeight: 48, display: 'inline-flex', alignItems: 'center' }}>
                        Remembered password? Click here
                    </Link>
                </Box>
            ) : (
                <Box display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="90vh"
                >
                    <Typography>Invalid URL. Please go to your email and click the link again.</Typography>
                    <Link component={RouterLink} to="/login" variant="body2" sx={{ mt: 1, minHeight: 48, display: 'inline-flex', alignItems: 'center' }}>
                        Remembered password? Click here
                    </Link>
                </Box>
            )}
        </Container>
    );
};

export default ResetPassword;
